import React, { useState } from "react";
import { parseLocalDate } from "utils/dateUtil";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { IconButton, Chip } from "@mui/material";
import { Edit, Delete, Warning, BugReport, Build } from "components/icons";

const getStatusColor = (status) => {
  const colors = {
    OPEN: "warning",
    IN_PROGRESS: "info",
    CLOSED: "success",
    OVERDUE: "error",
    PENDING_VERIFICATION: "secondary",
  };
  return colors[status] || "default";
};

const getPriorityColor = (priority) => {
  const colors = {
    LOW: "success",
    MEDIUM: "info",
    HIGH: "warning",
    CRITICAL: "error",
  };
  return colors[priority] || "default";
};

const getTypeIcon = (type) => {
  switch (type) {
    case "CORRECTIVE": return <BugReport />;
    case "PREVENTIVE": return <Build />;
    default: return <Warning />;
  }
};

export const CapaDataGrid = ({ capas, isLoading, onEdit, onDelete, showActions = true }) => {
  const [pageSize, setPageSize] = useState(10);

  const columns = [
    ...(showActions ? [{
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <IconButton key="edit" onClick={() => onEdit(params.row)} color="primary" size="small">
          <Edit />
        </IconButton>,
        <IconButton key="delete" onClick={() => onDelete(params.row)} color="error" size="small">
          <Delete />
        </IconButton>,
      ],
    }] : []),
    { field: "id", headerName: "ID", width: 80 },
    { field: "capaCode", headerName: "Code", width: 120 },
    { field: "title", headerName: "Title", width: 200, flex: 1 },
    {
      field: "type",
      headerName: "Type",
      width: 130,
      renderCell: (params) => (
        <Chip
          icon={getTypeIcon(params.value)}
          label={params.value}
          color={params.value === "CORRECTIVE" ? "error" : "info"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "priority",
      headerName: "Priority",
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getPriorityColor(params.value)}
          size="small"
        />
      ),
    },
    { field: "assignee", headerName: "Assignee", width: 140 },
    {
      field: "dueDate",
      headerName: "Due Date",
      width: 120,
      valueFormatter: (value) => value ? parseLocalDate(value).toLocaleDateString() : "N/A",
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value?.replace("_", " ")}
          color={getStatusColor(params.value)}
          size="small"
        />
      ),
    },
  ];

  return (
    <MDBox pt={3} py={3} px={2} height={400}>
      <DataGrid
        rows={capas || []}
        columns={columns}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={[5, 10, 20, 50]}
        loading={isLoading}
        disableSelectionOnClick
        getRowId={(row) => row.id || row.capaId}
      />
    </MDBox>
  );
};

CapaDataGrid.propTypes = {
  capas: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showActions: PropTypes.bool,
};

export default CapaDataGrid;

